import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { testesService, aulasService } from '../services/database'
import { supabase } from '../lib/supabaseClient'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  PlayIcon
} from '@heroicons/react/24/outline'
import type { Database } from '../lib/supabaseClient'

type Teste = Database['public']['Tables']['testes']['Row']
type TesteInsert = Database['public']['Tables']['testes']['Insert']
type Aula = Database['public']['Tables']['aulas']['Row']

const TestesPage = () => {
  const { user, profile } = useAuth()
  const [testes, setTestes] = useState<Teste[]>([])
  const [aulas, setAulas] = useState<Aula[]>([])
  const [questionCounts, setQuestionCounts] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingTeste, setEditingTeste] = useState<Teste | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    aula_id: '',
    tempo_limite: ''
  })

  useEffect(() => {
    fetchData()
  }, [])

  // Carregar dados reais do banco
  const fetchData = async () => {
    try {
      setLoading(true)
      const [testesData, aulasData, questionsData] = await Promise.all([
        testesService.getAll(),
        aulasService.getAll(),
        supabase.from('questoes').select('teste_id')
      ])

      const counts: Record<string, number> = {}
      if (questionsData.data) {
        questionsData.data.forEach((q: any) => {
          if (q.teste_id) {
            counts[q.teste_id] = (counts[q.teste_id] || 0) + 1
          }
        })
      }

      setTestes(testesData)
      setAulas(aulasData)
      setQuestionCounts(counts)
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredTestes = testes.filter(teste =>
    teste.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teste.description?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingTeste) {
        // Atualizar teste existente
        const updatedTeste = await testesService.update(editingTeste.id, {
          title: formData.title,
          description: formData.description || null,
          aula_id: formData.aula_id || null,
          tempo_limite: formData.tempo_limite ? parseInt(formData.tempo_limite) : null
        })

        if (updatedTeste) {
          setTestes(prev => prev.map(teste =>
            teste.id === editingTeste.id ? updatedTeste : teste
          ))
        }
      } else {
        // Criar novo teste
        const newTesteData: TesteInsert = {
          title: formData.title,
          description: formData.description || null,
          aula_id: formData.aula_id || null,
          tempo_limite: formData.tempo_limite ? parseInt(formData.tempo_limite) : null,
          is_published: false,
          created_by: user?.id || null
        }

        const newTeste = await testesService.create(newTesteData)
        if (newTeste) {
          setTestes(prev => [newTeste, ...prev])
        }
      }

      resetForm()
      setShowModal(false)
    } catch (error) {
      console.error('Erro ao salvar teste:', error)
    }
  }

  const handleEdit = (teste: Teste) => {
    setEditingTeste(teste)
    setFormData({
      title: teste.title,
      description: teste.description || '',
      aula_id: teste.aula_id || '',
      tempo_limite: teste.tempo_limite?.toString() || ''
    })
    setShowModal(true)
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este teste?')) {
      try {
        await testesService.delete(id)
        setTestes(prev => prev.filter(teste => teste.id !== id))
      } catch (error) {
        console.error('Erro ao excluir teste:', error)
      }
    }
  }

  const handleTogglePublish = async (teste: Teste) => {
    try {
      const updatedTeste = await testesService.update(teste.id, {
        is_published: !teste.is_published
      })

      if (updatedTeste) {
        setTestes(prev => prev.map(t =>
          t.id === teste.id ? updatedTeste : t
        ))
      }
    } catch (error) {
      console.error('Erro ao alterar status do teste:', error)
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      aula_id: '',
      tempo_limite: ''
    })
    setEditingTeste(null)
  }

  const getAulaTitle = (aulaId: string | null) => {
    if (!aulaId) return 'Sem aula associada'
    const aula = aulas.find(a => a.id === aulaId)
    return aula?.title || 'Aula não encontrada'
  }

  const canCreateTeste = profile?.role === 'admin' || profile?.role === 'professor'
  const canEditTeste = (teste: Teste) => {
    return profile?.role === 'admin' ||
      (profile?.role === 'professor' && teste.created_by === user?.id)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Testes</h1>
          <p className="text-gray-600">Gerencie os testes do sistema</p>
        </div>
        {canCreateTeste && (
          <button
            onClick={() => {
              resetForm()
              setShowModal(true)
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Novo Teste</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar testes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Testes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTestes.map((teste) => (
          <div key={teste.id} className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-lg text-gray-900">{teste.title}</h3>
                <div className="flex items-center space-x-2">
                  {teste.is_published ? (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                      Publicado
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                      Rascunho
                    </span>
                  )}
                </div>
              </div>

              {teste.description && (
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">{teste.description}</p>
              )}

              <div className="text-sm text-gray-500">
                <p className="mb-1">Aula: {getAulaTitle(teste.aula_id)}</p>
                {teste.tempo_limite && (
                  <p className="flex items-center space-x-1">
                    <ClockIcon className="h-4 w-4" />
                    <span>{teste.tempo_limite} minutos</span>
                  </p>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="p-4 bg-gray-50">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-600">{questionCounts[teste.id] || 0}</div>
                  <div className="text-xs text-gray-500">Questões</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">0</div>
                  <div className="text-xs text-gray-500">Respostas</div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleTogglePublish(teste)}
                    className={`text-sm px-3 py-1 rounded-lg ${teste.is_published
                      ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                      : 'bg-green-100 text-green-800 hover:bg-green-200'
                      }`}
                  >
                    {teste.is_published ? 'Despublicar' : 'Publicar'}
                  </button>

                  <button
                    onClick={() => window.open(`/teste/${teste.id}`, '_blank')}
                    className="text-sm px-3 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg flex items-center gap-1"
                    title="Abrir Teste"
                  >
                    <PlayIcon className="h-4 w-4" />
                    <span>Abrir</span>
                  </button>
                </div>

                {canEditTeste(teste) && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(teste)}
                      className="text-gray-600 hover:text-blue-600"
                      title="Editar"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(teste.id)}
                      className="text-gray-600 hover:text-red-600"
                      title="Excluir"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredTestes.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg mb-2">Nenhum teste encontrado</div>
          {canCreateTeste && (
            <button
              onClick={() => {
                resetForm()
                setShowModal(true)
              }}
              className="text-blue-600 hover:text-blue-800"
            >
              Criar primeiro teste
            </button>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4">
              {editingTeste ? 'Editar Teste' : 'Novo Teste'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Aula Associada
                </label>
                <select
                  value={formData.aula_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, aula_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecione uma aula (opcional)</option>
                  {aulas.map((aula) => (
                    <option key={aula.id} value={aula.id}>
                      {aula.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tempo Limite (minutos)
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.tempo_limite}
                  onChange={(e) => setFormData(prev => ({ ...prev, tempo_limite: e.target.value }))}
                  placeholder="60"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false)
                    resetForm()
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingTeste ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default TestesPage